import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { StatusBadge, RiskBadge } from '../../components/ui/StatusBadge';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Send,
  ArrowUpDown,
  X,
  Check,
  Search,
  Database,
  List,
  Brain,
  AlertTriangle,
  Info,
  ChevronRight,
} from 'lucide-react';

type ContentStatus = 'draft' | 'pending' | 'published';

interface SymptomGroupWithCount {
  id: string;
  created_at: string;
  company_id?: string;
  name_no: string;
  name_en?: string;
  description?: string;
  order_index: number;
  status: ContentStatus;
  version?: number;
  is_active?: boolean;
  created_by?: string;
  approved_by?: string;
  updated_at?: string;
  symptom_count: number;
}

interface Symptom {
  id: string;
  group_id: string;
  name_no: string;
  name_en?: string;
  help_text?: string;
  risk_tag?: string;
  order_index?: number;
  status: ContentStatus;
  version?: number;
}

export function SymptomGroupManagement() {
  const navigate = useNavigate();
  const { profile, hasRole } = useAuth();
  const { setHeaderAction, setPageTitle } = useAdminLayoutContext();
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SymptomGroupWithCount | null>(null);
  const [filterStatus, setFilterStatus] = useState<ContentStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [groups, setGroups] = useState<SymptomGroupWithCount[] | undefined>(undefined);
  const [formData, setFormData] = useState({
    name_no: '',
    name_en: '',
    description: '',
    order_index: 0,
    status: 'draft' as ContentStatus,
  });

  // Symptom management state
  const [showSymptomsModal, setShowSymptomsModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<SymptomGroupWithCount | null>(null);
  const [showSymptomFormModal, setShowSymptomFormModal] = useState(false);
  const [editingSymptom, setEditingSymptom] = useState<Symptom | null>(null);
  const [groupSymptoms, setGroupSymptoms] = useState<Symptom[] | undefined>(undefined);
  const [symptomFormData, setSymptomFormData] = useState({
    name_no: '',
    help_text: '',
    risk_tag: '',
    order_index: 0,
  });

  const canEdit = hasRole(['superadmin', 'org_admin', 'editor']);
  const canApprove = hasRole(['superadmin', 'org_admin']);
  const isSuperAdmin = hasRole(['superadmin']);

  // Fetch groups with symptom counts
  const fetchGroups = useCallback(async () => {
    let query = supabase
      .from('symptom_groups')
      .select('*, symptoms(count)')
      .order('order_index', { ascending: true });

    if (filterStatus !== 'all') {
      query = query.eq('status', filterStatus);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching groups:', error);
      return;
    }

    const mapped = (data || []).map((g: any) => ({
      ...g,
      symptom_count: g.symptoms?.[0]?.count ?? 0,
    }));
    setGroups(mapped);
  }, [filterStatus]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Fetch symptoms for selected group
  const fetchGroupSymptoms = useCallback(async () => {
    if (!selectedGroup) {
      setGroupSymptoms(undefined);
      return;
    }

    const { data, error } = await supabase
      .from('symptoms')
      .select('*')
      .eq('group_id', selectedGroup.id)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching symptoms:', error);
      return;
    }
    setGroupSymptoms(data || []);
  }, [selectedGroup]);

  useEffect(() => {
    fetchGroupSymptoms();
  }, [fetchGroupSymptoms]);

  // Set page title and header action
  useEffect(() => {
    setPageTitle('Symptomgrupper');

    if (canEdit) {
      setHeaderAction(
        <button
          onClick={() => {
            setEditingGroup(null);
            setFormData({ name_no: '', name_en: '', description: '', order_index: groups?.length || 0, status: 'draft' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Ny symptomgruppe
        </button>
      );
    }

    return () => {
      setPageTitle(null);
      setHeaderAction(null);
    };
  }, [setHeaderAction, setPageTitle, canEdit, groups?.length]);

  const riskOptions = [
    { value: '', label: 'Ingen risiko' },
    { value: 'suicide', label: 'Selvmordsrisiko' },
    { value: 'violence', label: 'Voldsrisiko' },
    { value: 'self-harm', label: 'Selvskading' },
    { value: 'psychosis', label: 'Psykose' },
    { value: 'substance', label: 'Rusmisbruk' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingGroup) {
        const { error } = await supabase
          .from('symptom_groups')
          .update({
            name_no: formData.name_no,
            name_en: formData.name_en || null,
            description: formData.description || null,
            order_index: formData.order_index,
            status: formData.status,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingGroup.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('symptom_groups')
          .insert({
            name_no: formData.name_no,
            name_en: formData.name_en || null,
            description: formData.description || null,
            order_index: formData.order_index,
            status: formData.status,
          });

        if (error) throw error;
      }

      setShowModal(false);
      setEditingGroup(null);
      resetForm();
      fetchGroups();
    } catch (error: any) {
      console.error('Error saving symptom group:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleStatusChange = async (id: string, newStatus: ContentStatus) => {
    try {
      const { error } = await supabase
        .from('symptom_groups')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      fetchGroups();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleEdit = (group: SymptomGroupWithCount) => {
    setEditingGroup(group);
    setFormData({
      name_no: group.name_no,
      name_en: group.name_en || '',
      description: group.description || '',
      order_index: group.order_index,
      status: group.status === 'pending' ? 'draft' : group.status,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Er du sikker på at du vil slette denne symptomgruppen? Dette vil også slette alle tilhørende symptomer.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('symptom_groups')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchGroups();
    } catch (error: any) {
      console.error('Error deleting symptom group:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleReorder = async (id: string, direction: 'up' | 'down') => {
    if (!groups) return;

    const index = groups.findIndex(g => g.id === id);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === groups.length - 1)) {
      return;
    }

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const currentOrder = groups[index].order_index;
    const swapOrder = groups[swapIndex].order_index;

    try {
      await supabase
        .from('symptom_groups')
        .update({ order_index: swapOrder, updated_at: new Date().toISOString() })
        .eq('id', groups[index].id);

      await supabase
        .from('symptom_groups')
        .update({ order_index: currentOrder, updated_at: new Date().toISOString() })
        .eq('id', groups[swapIndex].id);

      fetchGroups();
    } catch (error: any) {
      console.error('Error reordering:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleSeedData = async () => {
    if (!confirm('Dette vil importere de 7 standard symptomgruppene fra det gamle systemet. Eksisterende grupper vil ikke bli overskrevet. Fortsette?')) {
      return;
    }

    try {
      // Seed functionality would need to be implemented as a Supabase edge function or RPC
      alert('Seed-funksjonalitet er ikke implementert ennå for Supabase.');
    } catch (error: any) {
      console.error('Error seeding data:', error);
      alert(`Feil ved import: ${error.message}`);
    }
  };

  // Symptom management handlers
  const handleManageSymptoms = (group: SymptomGroupWithCount) => {
    setSelectedGroup(group);
    setShowSymptomsModal(true);
  };

  const handleAddSymptom = () => {
    resetSymptomForm();
    setShowSymptomFormModal(true);
  };

  const handleEditSymptom = (symptom: Symptom) => {
    setEditingSymptom(symptom);
    setSymptomFormData({
      name_no: symptom.name_no,
      help_text: symptom.help_text || '',
      risk_tag: symptom.risk_tag || '',
      order_index: symptom.order_index || 0,
    });
    setShowSymptomFormModal(true);
  };

  const handleDeleteSymptom = async (id: string) => {
    if (!confirm('Er du sikker på at du vil slette dette symptomet? Dette vil også slette alle koblinger til tilnærminger.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('symptoms')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchGroupSymptoms();
    } catch (error: any) {
      console.error('Error deleting symptom:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleSymptomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedGroup) return;

    try {
      if (editingSymptom) {
        const { error } = await supabase
          .from('symptoms')
          .update({
            group_id: selectedGroup.id,
            name_no: symptomFormData.name_no,
            help_text: symptomFormData.help_text || null,
            risk_tag: symptomFormData.risk_tag || null,
            order_index: symptomFormData.order_index,
          })
          .eq('id', editingSymptom.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('symptoms')
          .insert({
            group_id: selectedGroup.id,
            name_no: symptomFormData.name_no,
            help_text: symptomFormData.help_text || null,
            risk_tag: symptomFormData.risk_tag || null,
            order_index: symptomFormData.order_index,
          });

        if (error) throw error;
      }

      setShowSymptomFormModal(false);
      setEditingSymptom(null);
      resetSymptomForm();
      fetchGroupSymptoms();
      fetchGroups(); // refresh counts
    } catch (error: any) {
      console.error('Error saving symptom:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleSymptomStatusChange = async (id: string, newStatus: ContentStatus) => {
    try {
      const { error } = await supabase
        .from('symptoms')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      fetchGroupSymptoms();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name_no: '',
      name_en: '',
      description: '',
      order_index: groups ? groups.length : 0,
      status: 'draft',
    });
  };

  const resetSymptomForm = () => {
    setSymptomFormData({
      name_no: '',
      help_text: '',
      risk_tag: '',
      order_index: groupSymptoms ? groupSymptoms.length : 0,
    });
  };

  const formatDate = (timestamp?: string) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  const getStatusBadge = (status: ContentStatus, groupId?: any, canToggle: boolean = false) => {
    const handleClick = () => {
      if (canToggle && groupId && canEdit) {
        const newStatus = (status === 'published') ? 'draft' : 'published';
        handleStatusChange(groupId, newStatus);
      }
    };

    const clickableClass = canToggle && canEdit ? 'cursor-pointer hover:opacity-80 transition-opacity' : '';

    switch (status) {
      case 'draft':
      case 'pending': // Treat pending as draft
        return (
          <span
            onClick={handleClick}
            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-md ${clickableClass}`}
            title={canToggle && canEdit ? 'Klikk for å publisere' : undefined}
          >
            <Clock className="h-3 w-3" />
            Utkast
          </span>
        );
      case 'published':
        return (
          <span
            onClick={handleClick}
            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-md ${clickableClass}`}
            title={canToggle && canEdit ? 'Klikk for å avpublisere' : undefined}
          >
            <CheckCircle className="h-3 w-3" />
            Publisert
          </span>
        );
    }
  };

  // Filter groups based on search
  const filteredGroups = groups?.filter(group => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      group.name_no.toLowerCase().includes(search) ||
      group.description?.toLowerCase().includes(search)
    );
  });

  if (groups === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Laster symptomgrupper...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex items-center gap-4 flex-1">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
            <div className="flex gap-2">
              {(['all', 'draft', 'published'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    filterStatus === status
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {status === 'all' ? 'Alle' : status === 'draft' ? 'Utkast' : 'Publisert'}
                </button>
              ))}
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Søk etter gruppe..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Groups table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Rekkefølge
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Gruppe
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Symptomer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Sist oppdatert
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Versjon
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Handlinger
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredGroups && filteredGroups.length > 0 ? (
              filteredGroups.map((group, index) => (
                <tr
                  key={group.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  onClick={() => navigate(`/admin/symptom-groups/${group.id}/symptoms`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleReorder(group.id, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:text-gray-500 dark:hover:text-gray-300"
                        title="Flytt opp"
                      >
                        <ArrowUpDown className="h-4 w-4" />
                      </button>
                      <span className="font-medium text-gray-900 dark:text-white">{group.order_index}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-blue-100 dark:bg-blue-900/50">
                        <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">{group.name_no}</div>
                        {group.description && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-md">{group.description}</div>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                      {group.symptom_count ?? 0} symptomer
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {getStatusBadge(group.status, group.id, true)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(group.updated_at)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 dark:text-gray-400">v{group.version || 1}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      {canEdit && (
                        <>
                          <button
                            onClick={() => handleEdit(group)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-500 dark:hover:text-blue-400"
                            title="Rediger gruppe"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(group.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-500 dark:hover:text-red-400"
                            title="Slett gruppe"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <FileText className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">
                    {searchTerm ? 'Ingen symptomgrupper matcher søket' : 'Ingen symptomgrupper funnet'}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    {canEdit && !searchTerm ? 'Opprett din første symptomgruppe eller importer standard-grupper' : ''}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingGroup(null);
          resetForm();
        }}
        title={editingGroup ? 'Rediger symptomgruppe' : 'Legg til ny symptomgruppe'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name_no" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Navn *
            </label>
            <input
              id="name_no"
              type="text"
              required
              value={formData.name_no}
              onChange={(e) => setFormData({ ...formData, name_no: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="F.eks. Nedstemthet"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Beskrivelse
            </label>
            <textarea
              id="description"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Beskriv når denne gruppen skal brukes"
            />
          </div>

          <div>
            <label htmlFor="order_index" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Rekkefølge
            </label>
            <input
              id="order_index"
              type="number"
              min="0"
              value={formData.order_index}
              onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="draft"
                  checked={formData.status === 'draft'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ContentStatus })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-gray-700 dark:text-gray-300">Utkast</span>
                <span className="text-xs text-gray-500">(kun synlig for admin)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="published"
                  checked={formData.status === 'published'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ContentStatus })}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                />
                <span className="text-gray-700 dark:text-gray-300">Publisert</span>
                <span className="text-xs text-gray-500">(synlig for alle)</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setEditingGroup(null);
                resetForm();
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Check className="h-4 w-4" />
              {editingGroup ? 'Oppdater' : 'Opprett'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Symptoms Management Modal */}
      <Modal
        isOpen={showSymptomsModal}
        onClose={() => {
          setShowSymptomsModal(false);
          setSelectedGroup(null);
        }}
        title={`Symptomer for ${selectedGroup?.name_no || ''}`}
        size="xl"
      >
        <div className="space-y-4">
          {/* Header with Add button */}
          <div className="flex justify-between items-center pb-3 border-b dark:border-gray-700">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {groupSymptoms?.length || 0} symptomer i denne gruppen
              </p>
            </div>
            {canEdit && (
              <button
                onClick={handleAddSymptom}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                <Plus className="h-4 w-4" />
                Legg til symptom
              </button>
            )}
          </div>

          {/* Symptoms list */}
          {groupSymptoms === undefined ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Laster symptomer...
            </div>
          ) : groupSymptoms.length === 0 ? (
            <div className="text-center py-12">
              <Brain className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Ingen symptomer lagt til ennå</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Klikk "Legg til symptom" for å komme i gang
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {groupSymptoms.map((symptom) => (
                <div
                  key={symptom.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {symptom.name_no}
                      </span>
                      {symptom.risk_tag && <RiskBadge riskTag={symptom.risk_tag} />}
                      {/* Clickable status badge */}
                      {symptom.status === 'published' ? (
                        <span
                          onClick={() => canEdit && handleSymptomStatusChange(symptom.id, 'draft')}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-md ${canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                          title={canEdit ? 'Klikk for å avpublisere' : undefined}
                        >
                          <CheckCircle className="h-3 w-3" />
                          Publisert
                        </span>
                      ) : (
                        <span
                          onClick={() => canEdit && handleSymptomStatusChange(symptom.id, 'published')}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-md ${canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                          title={canEdit ? 'Klikk for å publisere' : undefined}
                        >
                          <Clock className="h-3 w-3" />
                          Utkast
                        </span>
                      )}
                    </div>
                    {symptom.help_text && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-start gap-1">
                        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        {symptom.help_text}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {canEdit && (
                      <>
                        <button
                          onClick={() => handleEditSymptom(symptom)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-500 dark:hover:text-blue-400"
                          title="Rediger"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSymptom(symptom.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-500 dark:hover:text-red-400"
                          title="Slett"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Close button */}
          <div className="flex justify-end pt-4 border-t dark:border-gray-700">
            <button
              onClick={() => {
                setShowSymptomsModal(false);
                setSelectedGroup(null);
              }}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Lukk
            </button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Symptom Modal */}
      <Modal
        isOpen={showSymptomFormModal}
        onClose={() => {
          setShowSymptomFormModal(false);
          setEditingSymptom(null);
          resetSymptomForm();
        }}
        title={editingSymptom ? 'Rediger symptom' : 'Legg til nytt symptom'}
        size="lg"
      >
        <form onSubmit={handleSymptomSubmit} className="space-y-4">
          <div>
            <label htmlFor="symptom_name_no" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Navn *
            </label>
            <input
              id="symptom_name_no"
              type="text"
              required
              value={symptomFormData.name_no}
              onChange={(e) => setSymptomFormData({ ...symptomFormData, name_no: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="F.eks. Søvnproblemer"
            />
          </div>

          <div>
            <label htmlFor="symptom_help_text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hjelpetekst
            </label>
            <textarea
              id="symptom_help_text"
              rows={2}
              value={symptomFormData.help_text}
              onChange={(e) => setSymptomFormData({ ...symptomFormData, help_text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Kort forklaring av symptomet..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="symptom_risk_tag" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Risiko-tag
              </label>
              <select
                id="symptom_risk_tag"
                value={symptomFormData.risk_tag}
                onChange={(e) => setSymptomFormData({ ...symptomFormData, risk_tag: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {riskOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="symptom_order_index" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rekkefølge
              </label>
              <input
                id="symptom_order_index"
                type="number"
                min="0"
                value={symptomFormData.order_index}
                onChange={(e) => setSymptomFormData({ ...symptomFormData, order_index: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {symptomFormData.risk_tag && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800 dark:text-yellow-200">
                  <p className="font-medium">Viktig om risiko-tags:</p>
                  <p className="mt-1">
                    Symptomer med risiko-tag vil utløse varsler i vurderingen når de velges med høy intensitet.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowSymptomFormModal(false);
                setEditingSymptom(null);
                resetSymptomForm();
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Check className="h-4 w-4" />
              {editingSymptom ? 'Oppdater' : 'Opprett'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
